import './course-video.css';
import { useState, useRef, useEffect } from 'react';
import { getContentUrl } from '../../services/course-service';
import { Box, Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import InterVideoQuestions from '../inter-video-questions/inter-video-questions';
import jwtDecode from 'jwt-decode';
import CustomProgressBar from './CustomProgressBar';

const CourseVideo = (props) => {
  const { course, afterVideo } = props;
  const navigate = useNavigate();
  const { section, empCourseId } = useParams();
  const userDetails = jwtDecode(localStorage.getItem('authToken'));

  const [disableNext, setDisableNext] = useState(true);
  const [showQuestions, setShowQuestions] = useState(false);
  const [pauseState, setPauseState] = useState(false);
  const [progress, setProgress] = useState(0);
  const [allowedSeekTime, setAllowedSeekTime] = useState(0);
  const videoContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const videoRef = useRef(null);
  const currentSection = course?.sections?.find(
    s => encodeURI(s.description) === encodeURI(section)
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        handlePause();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pauseState]);

  const onVideoEnd = () => {
    const completedSections = course.sections.findIndex(
      s => encodeURI(s.description) === encodeURI(section)
    ) + 1;

    const progressPercent = Math.round(
      completedSections * 100 / (course.sections.length + 1)
    );

    const nextSection = completedSections < course.sections.length
      ? encodeURI(course.sections[completedSections].description)
      : 'quiz';

    afterVideo(nextSection, progressPercent);
    setDisableNext(false);
  };

  const onNext = () => setShowQuestions(true);
  const onPreviousFromQuestions = () => setShowQuestions(false);

  const onNextFromQuestions = () => {
    const currentSectionIndex = course.sections.findIndex(
      s => encodeURI(s.description) === encodeURI(section)
    );
    if (currentSectionIndex !== course.sections.length - 1) {
      setShowQuestions(false);
      setDisableNext(true);
      navigate(
        `/course/${empCourseId}/${encodeURI(course.sections[currentSectionIndex + 1].description)}`
      );
    } else {
      navigate(`/course/${empCourseId}/quiz`);
    }
  };

  const onBeginQuizFromQuestions = () => {
    navigate(`/course/${empCourseId}/quiz`);
  };

  const handlePause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (pauseState) {
      video.play();
      setPauseState(false);
    } else {
      video.pause();
      setPauseState(true);
    }
  };

  const handleSkipToBeginning = () => {
    if (videoRef.current) {
      setPauseState(false);
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video && video.duration) {
      const current = video.currentTime;
      const duration = video.duration;
      setProgress((current / duration) * 100);
      setAllowedSeekTime(current);
    }
  };

  const handleSeek = (percent) => {
    const video = videoRef.current;
    if (video && video.duration) {
      const desiredTime = percent * video.duration;
      if (desiredTime <= allowedSeekTime) {
        video.currentTime = desiredTime;
      }
    }
  };

  return (
    <div>
      {!showQuestions ? (
        <div>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
            <h3>{currentSection?.description}</h3>
            <Button variant='contained' color='primary' onClick={() => navigate('/home')}>Exit</Button>
          </Box>
          <div className='p-2'></div>
          <p>{currentSection?.scenario}</p>
          <p style={{ fontWeight: 'bold' }}>
            Approximate duration: {currentSection?.content.duration} mins
          </p>
          <p style={{ fontWeight: 'bold', fontSize: '90%', color: '#ff0000' }}>
            * The "Next" button will remain disabled until the video is viewed completely.
          </p>
          <div className='p-2'></div>

          <div className='text-center'>
            {currentSection && (
              <div
                ref={videoContainerRef}
                className={`video-wrapper${isFullscreen ? ' fullscreen' : ''}`}
              >
                <div className="video-inner">
                  <video
                    ref={videoRef}
                    className="video-element"
                    src={getContentUrl(currentSection.content.content[0])}
                    preload="auto"
                    autoPlay
                    onEnded={onVideoEnd}
                    controls={false}
                    controlsList="nodownload"
                    onContextMenu={(e) => e.preventDefault()}
                    onTimeUpdate={handleTimeUpdate}
                  />
                  <div className="video-controls-overlay">
                    <CustomProgressBar
                      progress={progress}
                      onSeek={handleSeek}
                      isPlaying={!pauseState}
                      onTogglePlay={handlePause}
                      currentTime={videoRef.current?.currentTime || 0}
                      duration={videoRef.current?.duration || 0}
                      videoRef={videoContainerRef}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className='p-2'></div>
          <div className='text-center'>
            {userDetails?.features?.pauseAndRewind && (
              <>
                <Button variant='contained' color='secondary' onClick={handleSkipToBeginning}>
                  Skip to Beginning
                </Button>
                <span className='p-1'></span>
              </>
            )}
            <Button variant='contained' color='error' onClick={onNext} disabled={disableNext}>
              Next
            </Button>
          </div>
        </div>
      ) : (
        <InterVideoQuestions
          previous={onPreviousFromQuestions}
          next={onNextFromQuestions}
          beginQuiz={onBeginQuizFromQuestions}
          course={course}
        />
      )}
    </div>
  );
};

export default CourseVideo;
